import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";

/**
 * Firebase SDK bootstrap for HellocashflowAdmin.
 *
 * @remarks
 * This module must stay dependency-light. Importing the Redux store here creates a circular dependency:
 * `firebase.ts -> store -> baseApi -> MemoizedAuth -> firebase.ts`, which can fail at runtime with
 * "Cannot access 'auth' before initialization".
 *
 * Store synchronization lives in `src/lib/firebase/authStoreSync.ts`.
 *
 * Config comes from Vite env vars; see `.env.example`.
 *
 * @see src/lib/firebase/MemoizedAuth.ts
 * @see src/lib/firebase/authStoreSync.ts
 */

// See: https://firebase.google.com/docs/web/learn-more#config-object
const config = {
    apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
    appId: import.meta.env.VITE_FIREBASE_APPID,
};

const app = initializeApp(config);
export const firestore = getFirestore(app);
export const auth = getAuth(app);

// Cloud Functions region is pinned to avoid accidental cross-region latency.
export const functions = getFunctions(app, "australia-southeast1");

export const subbed: Array<(user: User | null) => void> = [];

// Module-level loading flag - THIS is the key to preventing race conditions
let AUTH_IS_LOADING = true;

onAuthStateChanged(auth, (user: User | null) => {
    // First callback means auth state is determined - no more loading!
    AUTH_IS_LOADING = false;

    subbed.forEach(cb => cb(user ? { ...user } : null));
});

// Export function to check loading state
export const isAuthLoading = () => AUTH_IS_LOADING;

/**
 * Subscribe to Firebase auth state changes.
 *
 * @remarks
 * We fan out `onAuthStateChanged` to multiple subscribers to avoid importing the Redux store here.
 * Callers are responsible for unsubscribing.
 */
export const subScribeToAuthStateChange = (
    callback: (user: User | null) => void,
) => {
    subbed.push(callback);
    return () => {
        subbed.indexOf(callback) > -1 &&
            subbed.splice(subbed.indexOf(callback), 1);
    };
};

export const updateUserState = () => {
    subbed.forEach(cb => cb(auth.currentUser ? auth.currentUser : null));
};

let token: undefined | string = undefined;

export const setToken = (newToken?: string) => {
    token = newToken;
};

export const getToken = () => {
    return token;
};
